/**
 * Supplier Product Import Page
 *
 * Import products from CSV/XLS files with custom column mapping.
 * Supports AliExpress Affiliate export format and custom mappings.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import {
  ArrowLeft,
  Upload,
  FileSpreadsheet,
  CheckCircle,
  AlertCircle,
  Loader2,
  Save,
  Trash2,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';

interface ColumnMapping {
  product_id: string;
  product_name?: string;
  product_url?: string;
  image_url?: string;
  video_url?: string;
  description?: string;
  original_price?: string;
  sale_price?: string;
  discount?: string;
  currency?: string;
  commission_rate?: string;
  sales_count?: string;
  feedback_rating?: string;
  promotion_url?: string;
  coupon_code?: string;
  category?: string;
  supplier_name?: string;
  shipping_days?: string;
  stock_quantity?: string;
  sku?: string;
}

interface MappingPreset {
  id: string;
  name: string;
  platform: string;
  mapping: ColumnMapping;
  is_default: boolean;
}

interface ImportResult {
  job_id: string;
  status: string;
  total_items: number;
  processed_items: number;
  failed_items: number;
  results: {
    created: number;
    updated: number;
    skipped: number;
    linked: number;
  };
  errors?: string[];
  message: string;
}

// Field labels for the mapping UI
const FIELD_LABELS: Record<keyof ColumnMapping, string> = {
  product_id: 'Product ID *',
  product_name: 'Product Name',
  product_url: 'Product URL',
  image_url: 'Image URL',
  video_url: 'Video URL',
  description: 'Description',
  original_price: 'Original Price',
  sale_price: 'Sale/Discount Price',
  discount: 'Discount %',
  currency: 'Currency',
  commission_rate: 'Commission Rate %',
  sales_count: 'Sales Count',
  feedback_rating: 'Feedback/Rating',
  promotion_url: 'Promotion/Affiliate URL',
  coupon_code: 'Coupon Code',
  category: 'Category',
  supplier_name: 'Supplier Name',
  shipping_days: 'Shipping Days',
  stock_quantity: 'Stock Quantity',
  sku: 'SKU',
};

export default function SupplierImportPage() {
  const { t } = useTranslation('store');
  const { storeId, supplierId } = useParams<{ storeId: string; supplierId: string }>();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [supplierName, setSupplierName] = useState('');

  // File and data
  const [file, setFile] = useState<File | null>(null);
  const [detectedColumns, setDetectedColumns] = useState<string[]>([]);
  const [sampleData, setSampleData] = useState<Record<string, string>>({});
  const [rowCount, setRowCount] = useState(0);

  // Mapping
  const [savedPresets, setSavedPresets] = useState<MappingPreset[]>([]);
  const [defaultPresets, setDefaultPresets] = useState<MappingPreset[]>([]);
  const [selectedPreset, setSelectedPreset] = useState<string>('');
  const [currentMapping, setCurrentMapping] = useState<ColumnMapping>({ product_id: '' });
  const [showAdvancedMapping, setShowAdvancedMapping] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [savingPreset, setSavingPreset] = useState(false);

  // Options
  const [createProducts, setCreateProducts] = useState(true);
  const [updateExisting, setUpdateExisting] = useState(true);
  const [importAsAffiliate, setImportAsAffiliate] = useState(false);

  // Import result
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  // Load saved mappings
  const loadMappings = useCallback(async () => {
    if (!storeId || !supplierId) return;

    try {
      setLoading(true);
      const response = await api.get(
        `/api/ecommerce/stores/${storeId}/suppliers/${supplierId}/import`
      );

      if (!response.ok) throw new Error('Failed to load mappings');

      const data = await response.json() as {
        supplier_name: string;
        saved_mappings: MappingPreset[];
        default_presets: MappingPreset[];
      };

      setSupplierName(data.supplier_name);
      setSavedPresets(data.saved_mappings || []);
      setDefaultPresets(data.default_presets || []);

      // Auto-select default preset if available
      const defaultPreset = data.saved_mappings?.find(m => m.is_default);
      if (defaultPreset) {
        setSelectedPreset(defaultPreset.id);
        setCurrentMapping(defaultPreset.mapping);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [storeId, supplierId]);

  useEffect(() => {
    loadMappings();
  }, [loadMappings]);

  // Handle file selection
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setError(null);
    setImportResult(null);

    try {
      const content = await selectedFile.text();

      // Detect columns from first line
      const lines = content.trim().split(/\r?\n/);
      if (lines.length > 0) {
        const delimiter = lines[0].includes('\t') ? '\t' : ',';
        const headers = lines[0].split(delimiter).map(h => h.trim().replace(/^["']|["']$/g, ''));
        setDetectedColumns(headers);
        setRowCount(lines.length - 1);

        // Parse sample row for preview
        if (lines.length > 1) {
          const values = lines[1].split(delimiter).map(v => v.trim().replace(/^["']|["']$/g, ''));
          const sample: Record<string, string> = {};
          headers.forEach((h, i) => {
            sample[h] = values[i] || '';
          });
          setSampleData(sample);
        }

        // Try to auto-detect mapping based on column names
        autoDetectMapping(headers);
      }
    } catch (err) {
      setError('Failed to read file');
    }
  };

  // Auto-detect column mapping based on common column names
  const autoDetectMapping = (columns: string[]) => {
    const mapping: Partial<ColumnMapping> = {};
    const lowerColumns = columns.map(c => c.toLowerCase());

    // Map common column names
    const mappings: Record<string, keyof ColumnMapping> = {
      'productid': 'product_id',
      'product_id': 'product_id',
      'product id': 'product_id',
      'id': 'product_id',
      'image url': 'image_url',
      'image_url': 'image_url',
      'imageurl': 'image_url',
      'video url': 'video_url',
      'video_url': 'video_url',
      'product desc': 'description',
      'description': 'description',
      'product_desc': 'description',
      'origin price': 'original_price',
      'original price': 'original_price',
      'original_price': 'original_price',
      'price': 'original_price',
      'discount price': 'sale_price',
      'sale price': 'sale_price',
      'sale_price': 'sale_price',
      'discount': 'discount',
      'currency': 'currency',
      'direct linking commission rate (%)': 'commission_rate',
      'commission rate': 'commission_rate',
      'commission_rate': 'commission_rate',
      'sales180day': 'sales_count',
      'sales_count': 'sales_count',
      'positive feedback': 'feedback_rating',
      'feedback': 'feedback_rating',
      'rating': 'feedback_rating',
      'promotion url': 'promotion_url',
      'promotion_url': 'promotion_url',
      'affiliate url': 'promotion_url',
      'code name': 'coupon_code',
      'coupon_code': 'coupon_code',
      'coupon': 'coupon_code',
      'category': 'category',
      'name': 'product_name',
      'product name': 'product_name',
      'title': 'product_name',
      'product_name': 'product_name',
      'product title': 'product_name',
      'url': 'product_url',
      'product url': 'product_url',
      'product_url': 'product_url',
      'sku': 'sku',
      'stock': 'stock_quantity',
      'stock_quantity': 'stock_quantity',
      'quantity': 'stock_quantity',
      'shipping days': 'shipping_days',
      'shipping_days': 'shipping_days',
      'delivery days': 'shipping_days',
    };

    columns.forEach((col) => {
      const lowerCol = col.toLowerCase();
      const field = mappings[lowerCol];
      if (field && !mapping[field]) {
        mapping[field] = col;
      }
    });

    // Only set mapping if product_id was found
    if (mapping.product_id) {
      setCurrentMapping({ product_id: mapping.product_id, ...mapping } as ColumnMapping);

      // Check if it matches AliExpress Affiliate format
      if (lowerColumns.includes('productid') && lowerColumns.includes('promotion url')) {
        const aliPreset = defaultPresets.find(p => p.id === 'aliexpress_affiliate');
        if (aliPreset) {
          setSelectedPreset('aliexpress_affiliate');
        }
      }
    }
  };

  // Handle preset selection
  const handlePresetSelect = (presetId: string) => {
    setSelectedPreset(presetId);

    if (!presetId) {
      setCurrentMapping({ product_id: '' });
      return;
    }

    // Find in saved presets first
    let preset = savedPresets.find(p => p.id === presetId);
    if (!preset) {
      preset = defaultPresets.find(p => p.id === presetId);
    }

    if (preset) {
      setCurrentMapping(preset.mapping);
    }
  };

  // Update individual mapping field
  const updateMapping = (field: keyof ColumnMapping, value: string) => {
    setCurrentMapping(prev => ({
      ...prev,
      [field]: value || undefined,
    }));
    setSelectedPreset(''); // Clear preset when manually editing
  };

  // Save current mapping as preset
  const savePreset = async (setAsDefault = false) => {
    if (!presetName.trim() || !currentMapping.product_id) {
      setError('Please provide a name and map at least the Product ID field');
      return;
    }

    try {
      setSavingPreset(true);

      const response = await api.put(
        `/api/ecommerce/stores/${storeId}/suppliers/${supplierId}/import`,
        {
          name: presetName,
          platform: 'custom',
          mapping: currentMapping,
          is_default: setAsDefault,
        }
      );

      if (!response.ok) throw new Error('Failed to save preset');

      setSuccess('Mapping preset saved');
      setPresetName('');
      loadMappings();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSavingPreset(false);
    }
  };

  // Delete a saved preset
  const deletePreset = async (presetId: string) => {
    if (!confirm('Delete this mapping preset?')) return;

    try {
      const response = await api.put(
        `/api/ecommerce/stores/${storeId}/suppliers/${supplierId}/import`,
        { delete_preset_id: presetId }
      );

      if (!response.ok) throw new Error('Failed to delete');

      setSuccess('Preset deleted');
      if (selectedPreset === presetId) {
        setSelectedPreset('');
        setCurrentMapping({ product_id: '' });
      }
      loadMappings();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  // Run the import
  const handleImport = async () => {
    if (!file || !currentMapping.product_id) {
      setError('Please select a file and map at least the Product ID field');
      return;
    }

    try {
      setImporting(true);
      setError(null);
      setImportResult(null);

      const formData = new FormData();
      formData.append('file', file);
      formData.append('mapping', JSON.stringify(currentMapping));
      formData.append('options', JSON.stringify({
        create_products: createProducts,
        update_existing: updateExisting,
        import_as_affiliate: importAsAffiliate,
      }));

      // Use fetch directly for FormData (can't use api helper which sets Content-Type: application/json)
      const response = await fetch(
        `/api/ecommerce/stores/${storeId}/suppliers/${supplierId}/import`,
        {
          method: 'POST',
          body: formData,
          credentials: 'include', // Include cookies for auth
        }
      );

      const result = await response.json() as ImportResult;

      if (!response.ok) {
        throw new Error((result as { error?: string }).error || 'Import failed');
      }

      setImportResult(result);

      if (result.status === 'completed') {
        setSuccess(result.message);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Link
          to={`/store/${storeId}/suppliers/${supplierId}/products`}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Upload className="w-6 h-6" />
            {t('suppliers.importProducts', 'Import Products')}
          </h1>
          <p className="text-gray-500 dark:text-gray-400">
            {supplierName} - {t('suppliers.importFromFile', 'Import from CSV/XLS file')}
          </p>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 flex items-center gap-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={() => setError(null)} className="ml-auto">&times;</button>
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-400 flex items-center gap-2">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span>{success}</span>
        </div>
      )}

      {/* Step 1: File Upload */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-sm flex items-center justify-center">1</span>
          {t('suppliers.selectFile', 'Select File')}
        </h2>

        <div
          onClick={() => fileInputRef.current?.click()}
          className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center cursor-pointer hover:border-blue-500 dark:hover:border-blue-400 transition-colors"
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xls,.xlsx,.tsv,.txt"
            onChange={handleFileSelect}
            className="hidden"
          />
          <FileSpreadsheet className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          {file ? (
            <div>
              <p className="text-gray-900 dark:text-white font-medium">{file.name}</p>
              <p className="text-sm text-gray-500">{rowCount} rows detected</p>
            </div>
          ) : (
            <div>
              <p className="text-gray-600 dark:text-gray-300">
                {t('suppliers.dropFileHere', 'Click to select or drop file here')}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {t('suppliers.supportedFormats', 'Supports CSV, XLS, TSV files')}
              </p>
            </div>
          )}
        </div>

        {detectedColumns.length > 0 && (
          <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
              <strong>{t('suppliers.detectedColumns', 'Detected columns')}:</strong>
            </p>
            <div className="flex flex-wrap gap-2">
              {detectedColumns.map((col) => (
                <span
                  key={col}
                  className="px-2 py-1 bg-white dark:bg-gray-600 text-xs rounded border border-gray-200 dark:border-gray-500"
                  title={sampleData[col] ? `Sample: ${sampleData[col].substring(0, 50)}...` : undefined}
                >
                  {col}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Step 2: Column Mapping */}
      {detectedColumns.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-sm flex items-center justify-center">2</span>
            {t('suppliers.mapColumns', 'Map Columns')}
          </h2>

          {/* Preset Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('suppliers.usePreset', 'Use Mapping Preset')}
            </label>
            <div className="flex gap-2">
              <select
                value={selectedPreset}
                onChange={(e) => handlePresetSelect(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">{t('suppliers.customMapping', 'Custom mapping...')}</option>
                <optgroup label={t('suppliers.savedMappings', 'Saved Mappings')}>
                  {savedPresets.map((preset) => (
                    <option key={preset.id} value={preset.id}>
                      {preset.name} {preset.is_default && '(default)'}
                    </option>
                  ))}
                </optgroup>
                <optgroup label={t('suppliers.defaultPresets', 'Default Presets')}>
                  {defaultPresets.map((preset) => (
                    <option key={preset.id} value={preset.id}>{preset.name}</option>
                  ))}
                </optgroup>
              </select>
              {selectedPreset && savedPresets.some(p => p.id === selectedPreset) && (
                <button
                  onClick={() => deletePreset(selectedPreset)}
                  className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                  title={t('common.delete', 'Delete')}
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>

          {/* Essential Mapping Fields */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {FIELD_LABELS.product_id}
              </label>
              <select
                value={currentMapping.product_id || ''}
                onChange={(e) => updateMapping('product_id', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
              >
                <option value="">-- {t('suppliers.selectColumn', 'Select column')} --</option>
                {detectedColumns.map((col) => (
                  <option key={col} value={col}>{col}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {FIELD_LABELS.product_name}
              </label>
              <select
                value={currentMapping.product_name || ''}
                onChange={(e) => updateMapping('product_name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
              >
                <option value="">-- {t('suppliers.selectColumn', 'Select column')} --</option>
                {detectedColumns.map((col) => (
                  <option key={col} value={col}>{col}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {FIELD_LABELS.sale_price}
              </label>
              <select
                value={currentMapping.sale_price || ''}
                onChange={(e) => updateMapping('sale_price', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
              >
                <option value="">-- {t('suppliers.selectColumn', 'Select column')} --</option>
                {detectedColumns.map((col) => (
                  <option key={col} value={col}>{col}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {FIELD_LABELS.image_url}
              </label>
              <select
                value={currentMapping.image_url || ''}
                onChange={(e) => updateMapping('image_url', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
              >
                <option value="">-- {t('suppliers.selectColumn', 'Select column')} --</option>
                {detectedColumns.map((col) => (
                  <option key={col} value={col}>{col}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Advanced Mapping Toggle */}
          <button
            onClick={() => setShowAdvancedMapping(!showAdvancedMapping)}
            className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 mb-4"
          >
            {showAdvancedMapping ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            {t('suppliers.advancedMapping', 'Advanced Column Mapping')}
          </button>

          {showAdvancedMapping && (
            <div className="grid grid-cols-2 gap-4 mb-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              {(Object.keys(FIELD_LABELS) as Array<keyof ColumnMapping>)
                .filter(f => !['product_id', 'product_name', 'sale_price', 'image_url'].includes(f))
                .map((field) => (
                  <div key={field}>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      {FIELD_LABELS[field]}
                    </label>
                    <select
                      value={currentMapping[field] || ''}
                      onChange={(e) => updateMapping(field, e.target.value)}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800"
                    >
                      <option value="">--</option>
                      {detectedColumns.map((col) => (
                        <option key={col} value={col}>{col}</option>
                      ))}
                    </select>
                  </div>
                ))}
            </div>
          )}

          {/* Save Mapping */}
          <div className="flex items-center gap-2 pt-4 border-t border-gray-200 dark:border-gray-600">
            <input
              type="text"
              value={presetName}
              onChange={(e) => setPresetName(e.target.value)}
              placeholder={t('suppliers.presetName', 'Save as preset...')}
              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
            />
            <button
              onClick={() => savePreset(false)}
              disabled={savingPreset || !presetName.trim()}
              className="px-3 py-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg text-sm disabled:opacity-50 flex items-center gap-1"
            >
              <Save className="w-4 h-4" />
              {t('common.save', 'Save')}
            </button>
            <button
              onClick={() => savePreset(true)}
              disabled={savingPreset || !presetName.trim()}
              className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm disabled:opacity-50 hover:bg-blue-700"
            >
              {t('suppliers.saveAsDefault', 'Save as Default')}
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Import Options */}
      {detectedColumns.length > 0 && currentMapping.product_id && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-sm flex items-center justify-center">3</span>
            {t('suppliers.importOptions', 'Import Options')}
          </h2>

          <div className="space-y-3">
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={createProducts}
                onChange={(e) => setCreateProducts(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-blue-600"
              />
              <div>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {t('suppliers.createNewProducts', 'Create new products')}
                </span>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('suppliers.createNewProductsDesc', 'Create products in your store for items that don\'t exist yet')}
                </p>
              </div>
            </label>
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={updateExisting}
                onChange={(e) => setUpdateExisting(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-blue-600"
              />
              <div>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {t('suppliers.updateExisting', 'Update existing links')}
                </span>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('suppliers.updateExistingDesc', 'Update prices and info for products already linked')}
                </p>
              </div>
            </label>

            {/* Affiliate Product Option */}
            <div className="border-t border-gray-200 dark:border-gray-600 pt-3 mt-3">
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={importAsAffiliate}
                  onChange={(e) => setImportAsAffiliate(e.target.checked)}
                  className="w-4 h-4 rounded border-purple-300 text-purple-600"
                />
                <div>
                  <span className="text-sm font-medium text-purple-700 dark:text-purple-300 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                    </svg>
                    {t('suppliers.importAsAffiliate', 'Import as Affiliate Products')}
                  </span>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t('suppliers.importAsAffiliateDesc', 'Products will redirect customers to the affiliate link instead of adding to cart. Best for AliExpress Affiliate imports.')}
                  </p>
                </div>
              </label>
            </div>

            {importAsAffiliate && (
              <div className="mt-3 p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
                <p className="text-sm text-purple-700 dark:text-purple-300 flex items-start gap-2">
                  <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>
                    Affiliate products will show a "Buy Now" button that opens the affiliate link in a new tab.
                    Commission rates and promotion URLs from your file will be imported automatically.
                  </span>
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Import Result */}
      {importResult && (
        <div className={`mb-6 p-4 rounded-lg border ${
          importResult.status === 'completed'
            ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
            : 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
        }`}>
          <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
            {t('suppliers.importResult', 'Import Result')}
          </h3>
          <div className="grid grid-cols-4 gap-4 text-sm">
            <div>
              <div className="text-2xl font-bold text-green-600">{importResult.results.created}</div>
              <div className="text-gray-500">{t('suppliers.created', 'Created')}</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-blue-600">{importResult.results.linked}</div>
              <div className="text-gray-500">{t('suppliers.linked', 'Linked')}</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-orange-600">{importResult.results.updated}</div>
              <div className="text-gray-500">{t('suppliers.updated', 'Updated')}</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-500">{importResult.results.skipped}</div>
              <div className="text-gray-500">{t('suppliers.skipped', 'Skipped')}</div>
            </div>
          </div>
          {importResult.errors && importResult.errors.length > 0 && (
            <div className="mt-3 text-sm text-red-600 dark:text-red-400">
              <strong>{t('suppliers.errors', 'Errors')}:</strong>
              <ul className="list-disc list-inside mt-1">
                {importResult.errors.slice(0, 5).map((err, i) => (
                  <li key={i}>{err}</li>
                ))}
                {importResult.errors.length > 5 && (
                  <li>...and {importResult.errors.length - 5} more</li>
                )}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-between">
        <Link
          to={`/store/${storeId}/suppliers/${supplierId}/products`}
          className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          {t('common.cancel', 'Cancel')}
        </Link>

        <button
          onClick={handleImport}
          disabled={importing || !file || !currentMapping.product_id}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
        >
          {importing ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              {t('suppliers.importing', 'Importing...')}
            </>
          ) : (
            <>
              <Upload className="w-4 h-4" />
              {t('suppliers.startImport', 'Start Import')} ({rowCount} rows)
            </>
          )}
        </button>
      </div>
    </div>
  );
}
